/**
 * SwitchPortsTab — Inline port-card grid with direct configuration.
 * Embeds inside PortElementForm's "Puertos" tab for existing switches/panels.
 * Shows a visual card grid of all ports and an inline editor below.
 * Reuses the same PortMapModal logic for connections.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../services/api';
import SwitchTopologyCanvas from './SwitchTopologyCanvas';
import { vlanChip, vlanTitle } from '../utils/portVlan';
import {
  buildLocationMap,
  groupElementsByLocation,
  locationPath,
  locationRootId,
} from '../utils/locationTree';
import './SwitchPortsTab.css';

const CONNECTOR_TYPES = ['rj45', 'lc', 'st', 'sc', 'mtp', 'sfp', 'sfp+', 'qsfp'];
const CABLE_TYPES     = ['', 'cat5e', 'cat6', 'cat6a', 'cat7', 'fiber_om3', 'fiber_om4', 'fiber_sm', 'dac'];
const CABLE_COLORS    = ['', 'azul', 'rojo', 'verde', 'amarillo', 'naranja', 'morado', 'gris', 'blanco', 'negro'];
const SWITCH_SPEEDS   = ['', '100M', '1G', '2.5G', '10G', '25G', '40G', '100G'];
const RSTP_ROLES = {
  root:       { abbr: 'R',   label: 'Root',       bg: '#dcfce7', fg: '#15803d' },
  designated: { abbr: 'D',   label: 'Designated',  bg: '#dbeafe', fg: '#1d4ed8' },
  alternate:  { abbr: 'ALT', label: 'Alternate',   bg: '#fef3c7', fg: '#b45309' },
  backup:     { abbr: 'BKP', label: 'Backup',      bg: '#fef9c3', fg: '#92400e' },
  disabled:   { abbr: 'DIS', label: 'Disabled',    bg: '#f3f4f6', fg: '#6b7280' },
};
const EMPTY_FORM = {
  destination_type:       '',
  destination_outlet_id:  '',
  destination_element_id: '',
  destination_port_id:    '',
  uplink_text:            '',
  origin_kind:            'none',
  origin_label:           '',
  origin_circuit_id:      '',
  origin_demarc_id:       '',
  connection_type:        'rj45',
  cable_type:             '',
  cable_color:            '',
  cable_length_m:         '',
  notes:                  '',
  vlan:                   '',
  speed:                  '',
  rstp_role:              '',
  poe_enabled:            false,
  is_populated:           true,
};
const PORTS_PER_ROW = 12;

const ORIGIN_BADGE_STYLES = {
  wall_outlet: { bg: '#dcfce7', fg: '#166534', text: 'Pared' },
  fiber_run: { bg: '#e0f2fe', fg: '#075985', text: 'Fibra' },
  isp_circuit: { bg: '#fef3c7', fg: '#92400e', text: 'WAN' },
  isp_demarc: { bg: '#fee2e2', fg: '#991b1b', text: 'Demarc' },
  custom: { bg: '#ede9fe', fg: '#6d28d9', text: 'Origen' },
};

function SwitchPortsTab({ element, companyId, poeCapable, onPortsChanged }) {
  const [ports,         setPorts]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [selectedPort,  setSelectedPort]  = useState(null);
  const [editForm,      setEditForm]      = useState(EMPTY_FORM);
  const [saving,        setSaving]        = useState(false);
  const [saveError,     setSaveError]     = useState('');
  const [destPorts,        setDestPorts]        = useState([]);  const [loadingDestPorts, setLoadingDestPorts] = useState(false);
  const [wallOutlets,        setWallOutlets]        = useState([]);
  const [loadingWallOutlets, setLoadingWallOutlets] = useState(false);
  const [rackElements,       setRackElements]       = useState([]);
  const [apElements,         setApElements]         = useState([]);
  const [showAllLocations,   setShowAllLocations]   = useState(false);
  const [ispCircuits,        setIspCircuits]        = useState([]);
  const [ispDemarcs,         setIspDemarcs]         = useState([]);

  // Mirror panels (other racks)
  const [mirrorPanels,         setMirrorPanels]         = useState([]);
  const [loadingMirrorPanels,  setLoadingMirrorPanels]  = useState(false);

  // Bulk selection
  const [bulkMode,       setBulkMode]       = useState(false);
  const [selectedPorts,  setSelectedPorts]  = useState(new Set());
  const [bulkForm,       setBulkForm]       = useState({ label: '', speed: '', connection_type: '', cable_type: '', vlan: '', poe_enabled: '', is_populated: '' });
  const [bulkSaving,     setBulkSaving]     = useState(false);
  const [bulkError,      setBulkError]      = useState('');
  const [bulkOk,         setBulkOk]         = useState('');

  // Bulk create wall outlets
  const [locations,          setLocations]          = useState([]);
  // Árbol completo (incluye inactivas) solo para resolver la raíz de una
  // ubicación; el selector de creación de tomas sigue usando las activas.
  const [allLocations,       setAllLocations]       = useState([]);
  const [showCreateOutlets,  setShowCreateOutlets]  = useState(false);
  const [createOutletsForm,  setCreateOutletsForm]  = useState({ code_prefix: '', location_id: '', room: '', floor: '' });
  const [creatingOutlets,    setCreatingOutlets]    = useState(false);
  const [createOutletsError, setCreateOutletsError] = useState('');
  const [createOutletsOk,    setCreateOutletsOk]    = useState('');

  // Add extra ports
  const [addPortsOpen,   setAddPortsOpen]   = useState(false);
  const [addPortsForm,   setAddPortsForm]   = useState({ count: 2, connection_type: 'sfp', label_prefix: '' });
  const [addingPorts,    setAddingPorts]    = useState(false);
  const [addPortsError,  setAddPortsError]  = useState('');

  // Topology canvas
  const [showTopology, setShowTopology] = useState(false);

  // IPAM VLANs
  const [ipamVlans, setIpamVlans] = useState([]);

  const isSwitch = element?.element_type === 'switch';

  // ── Data loading ──────────────────────────────────────────────────────

  const fetchPorts = useCallback(async () => {
    if (!element?.id) return;
    try {
      setLoading(true);
      const data = await api.getPorts(element.id, companyId);
      setPorts(data.data || []);
    } catch (err) {
      console.error('Error loading ports:', err);
      setPorts([]);
    } finally { setLoading(false); }
  }, [element?.id, companyId]);

  useEffect(() => { fetchPorts(); }, [fetchPorts]);

  // Load locations for bulk-create wall outlets
  useEffect(() => {
    if (!companyId) return;
    api.getLocations('', companyId).then(r => setLocations(r.data || [])).catch(() => {});
  }, [companyId]);

  // Árbol completo: una ubicación inactiva no debe romper la cadena padre-hijo.
  useEffect(() => {
    if (!companyId) return;
    api.getLocations('', companyId, { includeInactive: true })
      .then(r => setAllLocations(r.data || []))
      .catch(() => setAllLocations([]));
  }, [companyId]);

  // Load rack elements for destination dropdown.
  // Se cargan TODOS los de la empresa y el filtro por raíz de ubicación se hace
  // al pintar: así se pueden unir equipos de plantas distintas de la misma sede.
  const elementId = element?.id;
  useEffect(() => {
    if (!elementId || !companyId) return;
    api.getElements(companyId)
      .then(r => setRackElements((r.data || []).filter(e => e.id !== elementId)))
      .catch(() => setRackElements([]));
  }, [elementId, companyId]);

  // Puntos de acceso WiFi: son otro destino válido para un puerto de switch
  // (AP cableado directamente al switch). No son elementos de rack, así que van
  // en su propia lista y con el valor prefijado (`ap:<id>`) para no chocar con
  // los ids de rack_elements.
  useEffect(() => {
    if (!companyId) return;
    api.getWifiAPs(companyId)
      .then(r => setApElements(r.data || []))
      .catch(() => setApElements([]));
  }, [companyId]);

  // Load destination ports (elemento de rack o AP, según el prefijo del valor)
  useEffect(() => {
    const raw = editForm.destination_element_id;
    if (!raw) { setDestPorts([]); return; }
    const [kind, rawId] = String(raw).includes(':') ? String(raw).split(':') : ['el', String(raw)];
    const targetId = parseInt(rawId, 10);
    if (!targetId) { setDestPorts([]); return; }
    setLoadingDestPorts(true);
    (kind === 'ap' ? api.getWifiAPPorts(targetId, companyId) : api.getPorts(targetId, companyId))
      .then(r => setDestPorts(r.data || []))
      .catch(() => setDestPorts([]))
      .finally(() => setLoadingDestPorts(false));
  }, [editForm.destination_element_id, companyId]);

  // Lazy-load mirror panels when user selects 'mirror' destination type
  useEffect(() => {
    if (editForm.destination_type !== 'mirror' || !companyId || isSwitch) return;
    if (mirrorPanels.length > 0 || loadingMirrorPanels) return;
    setLoadingMirrorPanels(true);
    api.getElements(companyId, { element_type: 'panel' })
      .then(r => setMirrorPanels((r.data || []).filter(e => e.id !== element.id)))
      .catch(() => setMirrorPanels([]))
      .finally(() => setLoadingMirrorPanels(false));
  }, [editForm.destination_type, companyId, isSwitch, element?.id, mirrorPanels.length, loadingMirrorPanels]);

  // Load wall outlets
  useEffect(() => {
    if (editForm.destination_type === 'wall' && wallOutlets.length === 0 && !loadingWallOutlets) {
      setLoadingWallOutlets(true);
      api.getWallOutlets(companyId)
        .then(r => setWallOutlets(r.data || []))
        .catch(() => setWallOutlets([]))
        .finally(() => setLoadingWallOutlets(false));
    }
  }, [editForm.destination_type, companyId, wallOutlets.length, loadingWallOutlets]);

  // Load IPAM VLANs for switch elements
  useEffect(() => {
    if (!isSwitch || !companyId) return;
    api.getIpamVlans(companyId)
      .then(r => setIpamVlans(r.data || []))
      .catch(() => setIpamVlans([]));
  }, [isSwitch, companyId]);

  useEffect(() => {
    if (isSwitch || !companyId) return;
    let cancelled = false;

    const loadOrigins = async () => {
      try {
        const circuitsRes = await api.getIspCircuits(companyId);
        const circuits = circuitsRes.data || [];
        if (cancelled) return;
        setIspCircuits(circuits);

        const demarcResponses = await Promise.all(
          circuits.map(async (circuit) => {
            try {
              const response = await api.getIspDemarcPoints(circuit.id, companyId);
              return response.data || [];
            } catch {
              return [];
            }
          })
        );

        if (!cancelled) {
          setIspDemarcs(demarcResponses.flat());
        }
      } catch {
        if (!cancelled) {
          setIspCircuits([]);
          setIspDemarcs([]);
        }
      }
    };

    loadOrigins();
    return () => { cancelled = true; };
  }, [isSwitch, companyId]);

  // ── Destinos: misma raíz de ubicación ─────────────────────────────────
  // La raíz es la Sede/Edificio superior: Planta 1 y Planta 2 cuelgan de la
  // misma y sus equipos se pueden conectar. El check amplía a otras sedes.
  const locationMap = useMemo(() => buildLocationMap(allLocations), [allLocations]);
  const currentRootId = useMemo(
    () => locationRootId(locationMap, element?.location_id),
    [locationMap, element?.location_id],
  );

  const selectedElementId = useMemo(() => {
    const raw = editForm.destination_element_id;
    if (!raw) return null;
    const [kind, id] = String(raw).includes(':') ? String(raw).split(':') : ['el', String(raw)];
    return kind === 'el' ? id : null;
  }, [editForm.destination_element_id]);

  const selectedApId = useMemo(() => {
    const raw = editForm.destination_element_id;
    if (!raw || !String(raw).startsWith('ap:')) return null;
    return String(raw).slice(3);
  }, [editForm.destination_element_id]);

  const destinationElements = useMemo(() => {
    const base = (rackElements || []).filter(e => e.id !== element?.id);
    if (showAllLocations || !currentRootId) return base;
    return base.filter(e => (
      locationRootId(locationMap, e.location_id) === currentRootId
      // La conexión ya guardada se mantiene visible aunque esté fuera de la raíz.
      || (selectedElementId != null && String(e.id) === String(selectedElementId))
    ));
  }, [rackElements, element?.id, showAllLocations, currentRootId, locationMap, selectedElementId]);

  const destinationGroups = useMemo(
    () => groupElementsByLocation(locationMap, destinationElements),
    [locationMap, destinationElements],
  );

  const destinationAps = useMemo(() => {
    const base = apElements || [];
    if (showAllLocations || !currentRootId) return base;
    return base.filter(a => (
      locationRootId(locationMap, a.location_id) === currentRootId
      || (selectedApId != null && String(a.id) === String(selectedApId))
    ));
  }, [apElements, showAllLocations, currentRootId, locationMap, selectedApId]);

  /**
   * Un puerto es «mirror» cuando el otro extremo es un patch panel de OTRO
   * rack. Antes se deducía de que el elemento no estuviera en la lista del
   * rack; ahora la lista trae toda la empresa, así que hay que comparar el
   * rack explícitamente para no perder el distintivo 🪞.
   */
  const isMirrorPort = useCallback((port) => {
    if (isSwitch || !port?.connection) return false;
    const other = port.connection.other_end;
    if (other?.port_type !== 'panel_port' || !other.element_id) return false;
    const otherElement = (rackElements || []).find(e => e.id === other.element_id);
    if (!otherElement) return true; // fuera del catálogo cargado
    if (otherElement.rack_id == null || element?.rack_id == null) return false;
    return String(otherElement.rack_id) !== String(element.rack_id);
  }, [isSwitch, rackElements, element?.rack_id]);

  // ── Port click → populate form ────────────────────────────────────────

  const handlePortClick = (port) => {
    setSelectedPort(port);
    setSaveError('');
    setDestPorts([]);
    const conn = port.connection;
    const backbone = port.backbone;
    let dest_type = '', outlet_id = '', elem_id = '', port_id = '', uplink_txt = '';

    if (backbone) {
      // Enlace al otro armario: no ocupa el latiguillo, se muestra aparte.
      dest_type = 'mirror';
      elem_id   = `el:${backbone.other_end?.element_id || ''}`;
      port_id   = String(backbone.other_end?.port_id || '');
    } else if (conn) {
      const other = conn.other_end;
      if (other.port_type === 'wall_outlet') {
        dest_type = 'wall'; outlet_id = String(other.port_id);
      } else if (isMirrorPort(port)) {
        dest_type = 'mirror';
        elem_id   = String(other.element_id || '');
        port_id   = String(other.port_id   || '');
      } else {
        dest_type = 'element';
        // El valor lleva prefijo porque los APs no son rack_elements (ids aparte)
        elem_id   = other.port_type === 'wifi_ap_port'
          ? `ap:${other.element_id}`
          : `el:${other.element_id || ''}`;
        port_id   = String(other.port_id   || '');
      }
    } else if (port.destination_type === 'uplink') {
      dest_type = 'uplink'; uplink_txt = port.connected_to || '';
    }

    const origin = port.origin || null;

    setEditForm({
      ...EMPTY_FORM,
      destination_type:       dest_type,
      destination_outlet_id:  outlet_id,
      destination_element_id: elem_id,
      destination_port_id:    port_id,
      uplink_text:            uplink_txt,
      origin_kind:            origin?.kind || 'none',
      origin_label:           origin?.label || '',
      origin_circuit_id:      origin?.circuit?.id ? String(origin.circuit.id) : '',
      origin_demarc_id:       origin?.demarc?.id ? String(origin.demarc.id) : '',
      connection_type:  !isSwitch && port.is_populated === false ? '' : (port.connection_type || 'rj45'),
      cable_type:       conn?.cable_type  || backbone?.cable_type || port.cable_type  || '',
      cable_color:      conn?.cable_color || backbone?.cable_color || '',
      cable_length_m:   conn?.cable_length_m != null
        ? String(conn.cable_length_m)
        : (backbone?.cable_length_m != null ? String(backbone.cable_length_m) : ''),
      notes:            conn?.notes       || backbone?.notes || port.notes       || '',
      vlan:      port.vlan      || '',
      speed:     port.speed     || '',
      rstp_role: port.rstp_role || '',
      poe_enabled: !!port.poe_enabled,
      is_populated: port.is_populated !== false,
    });
  };

  const setDestType = (type) => {
    setEditForm(f => ({ ...f, destination_type: type, destination_outlet_id: '', destination_element_id: '', destination_port_id: '', uplink_text: '' }));
    setDestPorts([]);
  };

  const setOriginKind = (kind) => {
    setEditForm(f => ({
      ...f,
      origin_kind: kind,
      origin_label: kind === 'fiber_run' || kind === 'custom' ? f.origin_label : '',
      origin_circuit_id: kind === 'isp_circuit' || kind === 'isp_demarc' ? f.origin_circuit_id : '',
      origin_demarc_id: kind === 'isp_demarc' ? f.origin_demarc_id : '',
    }));
  };

  // ── Save ──────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!selectedPort) return;
    const myPortType   = isSwitch ? 'switch_port' : 'panel_port';
    const existingConn = selectedPort.connection;
    const existingBackbone = selectedPort.backbone;
    const dt           = editForm.destination_type;

    // El mirror NO usa PortConnection: va por `backbone_links` para que la
    // boca del panel conserve su latiguillo al switch.
    const connChanging = dt === 'mirror' ? false : (() => {
      if (!existingConn && !dt) return false;
      if (!existingConn && dt)  return true;
      if (existingConn && !dt)  return true;
      if (existingConn && dt === 'uplink') return true;
      const other = existingConn.other_end;
      if (dt === 'wall') {
        const oid = parseInt(editForm.destination_outlet_id);
        return !(other.port_type === 'wall_outlet' && other.port_id === oid);
      }
      if (dt === 'element') return other.port_id !== parseInt(editForm.destination_port_id);
      return true;
    })();

    const backboneChanging = Boolean(existingBackbone) && (
      dt !== 'mirror'
      || String(existingBackbone.other_end?.port_id || '') !== String(editForm.destination_port_id || '')
    );

    try {
      setSaving(true); setSaveError('');

      if (existingConn && connChanging)
        await api.deleteConnection(existingConn.id, companyId);
      if (backboneChanging)
        await api.deleteCablingLink('backbone', existingBackbone.id, companyId);

      if (dt === 'mirror' && editForm.destination_port_id) {
        if (backboneChanging || !existingBackbone) {
          await api.createCablingLink(
            { type: 'panel_port', id: selectedPort.id },
            { type: 'panel_port', id: parseInt(editForm.destination_port_id) },
            {
              cable_type: editForm.cable_type || undefined,
              cable_color: editForm.cable_color || undefined,
              length_m: editForm.cable_length_m ? parseFloat(editForm.cable_length_m) : undefined,
              notes: editForm.notes || undefined,
            },
            companyId,
          );
        } else if (existingBackbone) {
          await api.updateCablingLink('backbone', existingBackbone.id, {
            cable_type: editForm.cable_type, cable_color: editForm.cable_color,
            length_m: editForm.cable_length_m ? parseFloat(editForm.cable_length_m) : null,
            notes: editForm.notes,
          }, companyId);
        }
      } else if (dt === 'element' && editForm.destination_port_id) {
        const dp = destPorts.find(p => String(p.id) === editForm.destination_port_id);
        if (connChanging) {
          await api.createConnection({
            port_a_type: myPortType, port_a_id: selectedPort.id,
            port_b_type: dp?.port_entity_type || 'panel_port',
            port_b_id: parseInt(editForm.destination_port_id),
            cable_type: editForm.cable_type || undefined, cable_color: editForm.cable_color || undefined,
            cable_length_m: editForm.cable_length_m ? parseFloat(editForm.cable_length_m) : undefined,
            notes: editForm.notes || undefined,
          }, companyId);
        } else if (existingConn) {
          await api.updateConnection(existingConn.id, {
            cable_type: editForm.cable_type, cable_color: editForm.cable_color,
            cable_length_m: editForm.cable_length_m ? parseFloat(editForm.cable_length_m) : null,
            notes: editForm.notes,
          }, companyId);
        }
      } else if (dt === 'wall' && editForm.destination_outlet_id) {
        if (connChanging) {
          await api.createConnection({
            port_a_type: myPortType, port_a_id: selectedPort.id,
            port_b_type: 'wall_outlet', port_b_id: parseInt(editForm.destination_outlet_id),
            cable_type: editForm.cable_type || undefined, cable_color: editForm.cable_color || undefined,
            cable_length_m: editForm.cable_length_m ? parseFloat(editForm.cable_length_m) : undefined,
            notes: editForm.notes || undefined,
          }, companyId);
        } else if (existingConn) {
          await api.updateConnection(existingConn.id, {
            cable_type: editForm.cable_type, cable_color: editForm.cable_color,
            cable_length_m: editForm.cable_length_m ? parseFloat(editForm.cable_length_m) : null,
            notes: editForm.notes,
          }, companyId);
        }
      }

      const portPayload = {
        connection_type: !isSwitch && editForm.is_populated === false ? null : editForm.connection_type,
        cable_type: editForm.cable_type,
        notes: editForm.notes,
        ...(isSwitch ? { vlan: editForm.vlan, speed: editForm.speed, rstp_role: editForm.rstp_role || null, poe_enabled: !!editForm.poe_enabled } : { is_populated: editForm.is_populated }),
      };
      if (!isSwitch) {
        portPayload.origin_kind = editForm.origin_kind || 'none';
        portPayload.origin_label = editForm.origin_label || '';
        portPayload.origin_circuit_id = editForm.origin_circuit_id ? Number(editForm.origin_circuit_id) : null;
        portPayload.origin_demarc_id = editForm.origin_demarc_id ? Number(editForm.origin_demarc_id) : null;
      }
      if (dt === 'uplink') {
        portPayload.destination_type = 'uplink';
        portPayload.connected_to = editForm.uplink_text;
        portPayload.is_connected = Boolean(editForm.uplink_text);
      } else if (!dt && isSwitch) {
        portPayload.destination_type = null;
        portPayload.connected_to = null;
      }
      await api.updatePort(element.id, selectedPort.id, portPayload, companyId);

      await fetchPorts();

      // Re-select same port to refresh data
      const refreshed = (await api.getPorts(element.id, companyId)).data || [];
      const updated = refreshed.find(p => p.id === selectedPort.id);
      if (updated) handlePortClick(updated);
      else setSelectedPort(null);

    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Error al guardar';
      setSaveError(msg.includes('destino') ? msg : `Error al guardar: ${msg}`);
    }
    finally { setSaving(false); }
  };

  const handleDisconnect = async () => {
    const conn = selectedPort?.connection;
    if (!conn) return;
    try {
      setSaving(true); setSaveError('');
      await api.deleteConnection(conn.id, companyId);
      await fetchPorts();
      setEditForm(f => ({ ...f, destination_type: '', destination_outlet_id: '', destination_element_id: '', destination_port_id: '' }));
    } catch (err) { setSaveError(err.message || 'Error al desconectar'); }
    finally { setSaving(false); }
  };

  // ── Bulk selection handlers ───────────────────────────────────────────

  const toggleBulkMode = () => {
    setBulkMode(m => !m);
    setSelectedPorts(new Set());
    setBulkError('');
    setBulkOk('');
    setBulkForm({ label: '', speed: '', connection_type: '', cable_type: '', vlan: '', poe_enabled: '', is_populated: '' });
    setShowCreateOutlets(false);
    setCreateOutletsOk('');
    if (selectedPort) setSelectedPort(null);
  };

  const togglePortInSelection = (port) => {
    setSelectedPorts(prev => {
      const next = new Set(prev);
      if (next.has(port.id)) next.delete(port.id);
      else next.add(port.id);
      return next;
    });
  };

  const selectAll = () => setSelectedPorts(new Set(ports.map(p => p.id)));
  const clearSelection = () => setSelectedPorts(new Set());

  const handleBulkApply = async () => {
    if (selectedPorts.size === 0) return;
    const payload = {};
    if (bulkForm.label)           payload.label           = bulkForm.label;
    if (bulkForm.speed)           payload.speed           = bulkForm.speed;
    if (bulkForm.connection_type) payload.connection_type = bulkForm.connection_type;
    if (bulkForm.cable_type)      payload.cable_type      = bulkForm.cable_type;
    if (bulkForm.vlan)            payload.vlan            = bulkForm.vlan;
    if (isSwitch && bulkForm.poe_enabled !== '') {
      payload.poe_enabled = bulkForm.poe_enabled === 'true';
    }
    if (!isSwitch && bulkForm.is_populated !== '') {
      payload.is_populated = bulkForm.is_populated === 'true';
      if (bulkForm.is_populated === 'false') {
        payload.connection_type = null;
      }
    }
    if (Object.keys(payload).length === 0) { setBulkError('Rellena al menos un campo para aplicar.'); return; }
    setBulkSaving(true); setBulkError(''); setBulkOk('');
    try {
      const res = await api.bulkUpdatePorts(element.id, Array.from(selectedPorts), payload, companyId);
      setBulkOk(`✓ Actualizado ${res.updated} puertos`);
      setBulkForm({ label: '', speed: '', connection_type: '', cable_type: '', vlan: '', poe_enabled: '', is_populated: '' });
      setSelectedPorts(new Set());
      await fetchPorts();
    } catch (e) { setBulkError(e.message || 'Error en operación masiva'); }
    finally { setBulkSaving(false); }
  };

  // ── Bulk create wall outlets ─────────────────────────────────────────

  const OUTLET_TYPES_SET = new Set(['rj45', 'rj45x2', 'fiber_sc', 'fiber_lc', 'fiber_mtp']);

  const handleCreateOutlets = async () => {
    if (selectedPorts.size === 0) return;
    setCreatingOutlets(true); setCreateOutletsError(''); setCreateOutletsOk('');
    const selectedPortObjs = ports.filter(p => selectedPorts.has(p.id));
    const prefix = createOutletsForm.code_prefix.trim();
    const isPanel = element?.element_type === 'panel';
    try {
      await Promise.all(selectedPortObjs.map(p => {
        const portLabel = p.label || `P${p.port_number}`;
        const code = prefix ? `${prefix}-${portLabel}` : portLabel;
        const outlet_type = OUTLET_TYPES_SET.has(p.connection_type) ? p.connection_type : 'rj45';
        const payload = {
          code,
          outlet_type,
          status:      'active',
          room:        createOutletsForm.room  || null,
          floor:       createOutletsForm.floor || null,
          location_id: createOutletsForm.location_id ? Number(createOutletsForm.location_id) : null,
          panel_code:  isPanel ? (element.code || element.name || '') : null,
          panel_id:    isPanel ? element.id : null,
          panel_port_id: isPanel ? p.id : null,
        };
        return api.createWallOutlet(payload, companyId);
      }));
      setCreateOutletsOk(`✓ ${selectedPortObjs.length} toma${selectedPortObjs.length !== 1 ? 's' : ''} de pared creada${selectedPortObjs.length !== 1 ? 's' : ''} correctamente`);
      setShowCreateOutlets(false);
      setCreateOutletsForm({ code_prefix: '', location_id: '', room: '', floor: '' });
      setSelectedPorts(new Set());
    } catch (e) { setCreateOutletsError(e.message || 'Error al crear tomas'); }
    finally { setCreatingOutlets(false); }
  };

  // ── Add extra ports ───────────────────────────────────────────────────

  const handleAddPorts = async () => {
    const count = parseInt(addPortsForm.count);
    if (!count || count < 1) { setAddPortsError('Indica un número válido de puertos'); return; }
    setAddingPorts(true); setAddPortsError('');
    try {
      const res = await api.addExtraPorts(element.id, count, addPortsForm.connection_type, addPortsForm.label_prefix, companyId);
      setAddPortsOpen(false);
      setAddPortsForm({ count: 2, connection_type: 'sfp', label_prefix: '' });
      await fetchPorts();
      // El elemento acaba de cambiar de `port_count` en el backend: si la ficha
      // conserva el valor viejo, al guardarla el backend recorta los puertos
      // nuevos (`_sync_switch_ports`). El padre tiene que enterarse.
      if (typeof res?.new_port_count === 'number') onPortsChanged?.(res.new_port_count);
    } catch (e) { setAddPortsError(e.message || 'Error al añadir puertos'); }
    finally { setAddingPorts(false); }
  };

  // ── Visual helpers ────────────────────────────────────────────────────

  const hasConnection = (p) => Boolean(p.connection);
  const hasUplink     = (p) => p.destination_type === 'uplink' && p.connected_to;

  const CONNECTOR_MAP = {
    rj45:   { abbr: 'RJ45',  bg: '#e5e7eb', fg: '#374151' },
    lc:     { abbr: 'LC',    bg: '#fef3c7', fg: '#92400e' },
    sc:     { abbr: 'SC',    bg: '#fef3c7', fg: '#92400e' },
    st:     { abbr: 'ST',    bg: '#fef3c7', fg: '#92400e' },
    mtp:    { abbr: 'MTP',   bg: '#fef3c7', fg: '#92400e' },
    fc:     { abbr: 'FC',    bg: '#fef3c7', fg: '#92400e' },
    sfp:    { abbr: 'SFP',   bg: '#dbeafe', fg: '#1e40af' },
    'sfp+': { abbr: 'SFP+',  bg: '#dbeafe', fg: '#1e40af' },
    qsfp:   { abbr: 'QSFP',  bg: '#dbeafe', fg: '#1e40af' },
  };
  const getConnector = (port) => {
    const ct = (port.connection_type || 'rj45').toLowerCase();
    return CONNECTOR_MAP[ct] || { abbr: ct.toUpperCase(), bg: '#e5e7eb', fg: '#374151' };
  };

  const getPortColor = (port) => {
    if (!isSwitch && port.is_populated === false) return 'spt-port--empty';
    if (!port.is_active) return 'spt-port--inactive';
    if (isMirrorPort(port)) return 'spt-port--mirror';
    if (port.connection?.other_end?.element_type === 'wall_outlet') return 'spt-port--outlet';
    if (hasConnection(port)) return 'spt-port--connected';
    if (hasUplink(port))     return 'spt-port--uplink';
    return 'spt-port--free';
  };

  const getPortTooltip = (port) => {
    if (!isSwitch && port.is_populated === false) return `Puerto ${port.port_number} · Slot vacío (sin conector)`;
    const conn = port.connection;
    const parts = [`Puerto ${port.port_number}`];
    if (conn) {
      const o = conn.other_end;
      if (o.port_type === 'wall_outlet') parts.push(`→ Toma ${o.label || ''}`);
      else if (isMirrorPort(port)) parts.push(`🪞 ${o.element_name || '?'} P${o.port_number ?? '?'}`);
      else parts.push(`→ ${o.element_name || '?'} P${o.port_number ?? '?'}`);
      if (conn.cable_type) parts.push(`[${conn.cable_type}]`);
    } else if (hasUplink(port)) {
      parts.push(`⬆ ${port.connected_to}`);
    } else {
      parts.push('Libre');
    }
    if (port.vlan) parts.push(`VLAN: ${port.vlan}`);
    if (port.speed) parts.push(port.speed);
    if (port.poe_enabled) parts.push('PoE');
    if (port.rstp_role && RSTP_ROLES[port.rstp_role]) parts.push(`RSTP: ${RSTP_ROLES[port.rstp_role].label}`);
    if (port.wall_outlet) parts.push(`🏢 ${port.wall_outlet.code}${port.wall_outlet.room ? ` (${port.wall_outlet.room})` : ''}`);
    return parts.join(' · ');
  };

  const getPortLabel = (port) => {
    const conn = port.connection;
    if (conn) {
      const other = conn.other_end;
      if (other.port_type === 'wall_outlet') return '→W';
      if (isMirrorPort(port)) return '🪞';
      return other.port_number != null ? `→${other.port_number}` : '→?';
    }
    if (hasUplink(port)) return '⬆';
    return '';
  };

  const getOriginBadge = (port) => {
    const kind = port.origin?.kind;
    return kind ? (ORIGIN_BADGE_STYLES[kind] || ORIGIN_BADGE_STYLES.custom) : null;
  };

  const getOriginPath = (port) => {
    const origin = port.origin;
    if (!origin) return null;
    if (origin.kind === 'wall_outlet') {
      return ['Patch panel', origin.wall_outlet?.code || origin.label || 'Toma'];
    }
    if (origin.kind === 'isp_demarc') {
      const parts = ['Patch panel'];
      if (origin.demarc?.physical_id || origin.demarc?.label) parts.push(origin.demarc?.physical_id || origin.demarc?.label);
      if (origin.circuit?.label || origin.circuit?.circuit_id_provider) parts.push(origin.circuit?.label || origin.circuit?.circuit_id_provider);
      return parts;
    }
    if (origin.kind === 'isp_circuit') {
      return ['Patch panel', origin.circuit?.label || origin.circuit?.circuit_id_provider || origin.label || 'Circuito WAN'];
    }
    return ['Patch panel', origin.label || 'Origen'];
  };

  const totalPorts     = ports.length;
  const connectedCount = ports.filter(p => hasConnection(p) || hasUplink(p)).length;
  const emptyCount     = !isSwitch ? ports.filter(p => p.is_populated === false).length : 0;
  const mirrorCount    = !isSwitch ? ports.filter(p => isMirrorPort(p)).length : 0;
  const freeCount      = ports.filter(p => !hasConnection(p) && !hasUplink(p) && p.is_active && p.is_populated !== false).length;
  const portRows = [];
  for (let i = 0; i < ports.length; i += PORTS_PER_ROW) portRows.push(ports.slice(i, i + PORTS_PER_ROW));

  // ── Render ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="spt-loading">
        <div className="spinner-border spinner-border-sm" role="status" />
        <span>Cargando puertos…</span>
      </div>
    );
  }

  if (ports.length === 0) {
    return (
      <div className="spt-empty">
        <span style={{ fontSize: 32 }}>🔌</span>
        <p>Sin puertos configurados.</p>
      </div>
    );
  }

  return (
    <div className="spt-container">

      {/* ── Stats bar ── */}
      <div className="spt-stats">
        <div className="spt-stat">
          <span className="spt-stat-dot spt-dot--connected" />
          <strong>{connectedCount}</strong> conectados
        </div>
        <div className="spt-stat">
          <span className="spt-stat-dot spt-dot--free" />
          <strong>{freeCount}</strong> libres
        </div>
        {!isSwitch && mirrorCount > 0 && (
          <div className="spt-stat">
            <span className="spt-stat-dot spt-dot--mirror" />
            <strong>{mirrorCount}</strong> mirror
          </div>
        )}
        {!isSwitch && emptyCount > 0 && (
          <div className="spt-stat">
            <span className="spt-stat-dot spt-dot--empty" />
            <strong>{emptyCount}</strong> slots vacíos
          </div>
        )}
        <div className="spt-stat">
          <strong>{totalPorts}</strong> total
        </div>
        <div className="spt-legend">
          <span className="spt-legend-item"><span className="spt-leg-swatch spt-dot--connected" />Conectado</span>
          <span className="spt-legend-item"><span className="spt-leg-swatch spt-dot--uplink" />Uplink</span>
          <span className="spt-legend-item"><span className="spt-leg-swatch spt-dot--free" />Libre</span>
          {!isSwitch && <span className="spt-legend-item"><span className="spt-leg-swatch spt-dot--mirror" />Mirror</span>}
          {!isSwitch && <span className="spt-legend-item"><span className="spt-leg-swatch spt-dot--empty" />Slot vacío</span>}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {isSwitch && (
            <button type="button" onClick={() => setShowTopology(v => !v)}
              className={`spt-toolbar-btn${showTopology ? ' active' : ''}`}>
              🌐 Topología
            </button>
          )}
          <button type="button" onClick={() => setAddPortsOpen(o => !o)}
            className={`spt-toolbar-btn${addPortsOpen ? ' active' : ''}`}>
            ➕ Añadir puertos
          </button>
          <button type="button" onClick={toggleBulkMode}
            className={`spt-toolbar-btn${bulkMode ? ' active' : ''}`}>
            {bulkMode ? `⨯ Cancelar selección` : '☑ Operación masiva'}
          </button>
        </div>
      </div>

      {/* ── Add extra ports panel ── */}
      {addPortsOpen && (
        <div className="spt-panel spt-panel--accent">
          <div className="spt-panel-title">➕ Añadir puertos adicionales</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <div className="spt-panel-label">Cantidad</div>
              <input type="number" min="1" max="48" value={addPortsForm.count}
                onChange={e => setAddPortsForm(f => ({ ...f, count: e.target.value }))}
                className="spt-field" style={{ width: 70 }} />
            </div>
            <div>
              <div className="spt-panel-label">Tipo conector</div>
              <select value={addPortsForm.connection_type}
                onChange={e => setAddPortsForm(f => ({ ...f, connection_type: e.target.value }))}
                className="spt-field">
                {CONNECTOR_TYPES.map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
              </select>
            </div>
            <div>
              <div className="spt-panel-label">Prefijo etiqueta (opcional)</div>
              <input type="text" placeholder="Ej: SFP" value={addPortsForm.label_prefix}
                onChange={e => setAddPortsForm(f => ({ ...f, label_prefix: e.target.value }))}
                className="spt-field" style={{ width: 100 }} />
            </div>
            <button type="button" onClick={handleAddPorts} disabled={addingPorts}
              className="spt-btn-primary">
              {addingPorts ? '⏳ Añadiendo…' : 'Añadir'}
            </button>
            <button type="button" onClick={() => setAddPortsOpen(false)}
              className="spt-btn-secondary">
              Cancelar
            </button>
          </div>
          {addPortsError && <div className="spt-panel-error">{addPortsError}</div>}
        </div>
      )}

      {/* ── Bulk action panel ── */}
      {bulkMode && (
        <div className="spt-panel spt-panel--bulk">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span className="spt-panel-title" style={{ marginBottom: 0 }}>☑ Operación masiva</span>
            <span className="spt-panel-hint">{selectedPorts.size} puertos seleccionados</span>
            <button type="button" onClick={selectAll} className="spt-toolbar-btn">Seleccionar todos</button>
            <button type="button" onClick={clearSelection} className="spt-toolbar-btn">Limpiar</button>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <div className="spt-panel-label">Etiqueta</div>
              <input type="text" placeholder="Ej: ILOs, MGMT…" value={bulkForm.label}
                onChange={e => setBulkForm(f => ({ ...f, label: e.target.value }))}
                className="spt-field" style={{ width: 110 }} />
            </div>
            {isSwitch && (
              <div>
                <div className="spt-panel-label">Velocidad</div>
                <select value={bulkForm.speed} onChange={e => setBulkForm(f => ({ ...f, speed: e.target.value }))}
                  className="spt-field">
                  <option value="">— sin cambio —</option>
                  {SWITCH_SPEEDS.filter(Boolean).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
            <div>
              {!isSwitch && (
                <>
                  <div className="spt-panel-label">Keystone</div>
                  <select value={bulkForm.is_populated} onChange={e => setBulkForm(f => ({ ...f, is_populated: e.target.value, connection_type: e.target.value === 'false' ? '' : f.connection_type }))}
                    className="spt-field" style={{ marginBottom: 8 }}>
                    <option value="">— sin cambio —</option>
                    <option value="true">Instalado</option>
                    <option value="false">No instalado</option>
                  </select>
                </>
              )}
            </div>
            <div>
              <div className="spt-panel-label">Conector</div>
              <select value={bulkForm.connection_type} onChange={e => setBulkForm(f => ({ ...f, connection_type: e.target.value }))}
                disabled={!isSwitch && bulkForm.is_populated === 'false'}
                className="spt-field">
                <option value="">— sin cambio —</option>
                {CONNECTOR_TYPES.map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
              </select>
              {!isSwitch && bulkForm.is_populated === 'false' && (
                <div className="spt-panel-hint" style={{ marginTop: 4 }}>Sin keystone no se asigna conector.</div>
              )}
            </div>
            <div>
              <div className="spt-panel-label">Tipo cable</div>
              <select value={bulkForm.cable_type} onChange={e => setBulkForm(f => ({ ...f, cable_type: e.target.value }))}
                className="spt-field">
                <option value="">— sin cambio —</option>
                {CABLE_TYPES.filter(Boolean).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {isSwitch && (
              <div>
                <div className="spt-panel-label">VLAN</div>
                <input list="bulk-vlan-list" type="text" placeholder="100, trunk…" value={bulkForm.vlan}
                  onChange={e => setBulkForm(f => ({ ...f, vlan: e.target.value }))}
                  className="spt-field" style={{ width: 120 }} />
                <datalist id="bulk-vlan-list">
                  {ipamVlans.map(v => <option key={v.id} value={String(v.vlan_id)}>{v.vlan_id} — {v.name}</option>)}
                </datalist>
              </div>
            )}
            {isSwitch && (
              <div>
                <div className="spt-panel-label">PoE</div>
                <select value={bulkForm.poe_enabled} onChange={e => setBulkForm(f => ({ ...f, poe_enabled: e.target.value }))}
                  className="spt-field">
                  <option value="">— sin cambio —</option>
                  <option value="true">⚡ Entrega PoE</option>
                  <option value="false">Sin PoE</option>
                </select>
              </div>
            )}
            <button type="button" onClick={handleBulkApply} disabled={bulkSaving || selectedPorts.size === 0}
              className="spt-btn-primary spt-btn-bulk">
              {bulkSaving ? '⏳ Aplicando…' : `Aplicar a ${selectedPorts.size} puerto${selectedPorts.size !== 1 ? 's' : ''}`}
            </button>
            <button type="button" onClick={() => { setShowCreateOutlets(v => !v); setCreateOutletsError(''); setCreateOutletsOk(''); }}
              disabled={selectedPorts.size === 0}
              className="spt-btn-success">
              🏢 Crear tomas de pared
            </button>
          </div>
          {bulkError && <div className="spt-panel-error">{bulkError}</div>}
          {bulkOk    && <div className="spt-panel-ok">{bulkOk}</div>}

          {/* ── Create wall outlets sub-panel ── */}
          {showCreateOutlets && (
            <div className="spt-panel spt-panel--success" style={{ marginTop: 10, marginBottom: 0 }}>
              <div className="spt-panel-title">🏢 Crear {selectedPorts.size} toma{selectedPorts.size !== 1 ? 's' : ''} de pared</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                  <div className="spt-panel-label">Prefijo código <span className="spt-panel-hint">(opcional)</span></div>
                  <input type="text" placeholder="Ej: T-A" value={createOutletsForm.code_prefix}
                    onChange={e => setCreateOutletsForm(f => ({ ...f, code_prefix: e.target.value }))}
                    className="spt-field" style={{ width: 90 }} />
                </div>
                <div>
                  <div className="spt-panel-label">Ubicación</div>
                  <select value={createOutletsForm.location_id}
                    onChange={e => setCreateOutletsForm(f => ({ ...f, location_id: e.target.value }))}
                    className="spt-field" style={{ minWidth: 140 }}>
                    <option value="">— sin ubicación —</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div>
                  <div className="spt-panel-label">Sala <span className="spt-panel-hint">(opcional)</span></div>
                  <input type="text" placeholder="Sala 3B" value={createOutletsForm.room}
                    onChange={e => setCreateOutletsForm(f => ({ ...f, room: e.target.value }))}
                    className="spt-field" style={{ width: 100 }} />
                </div>
                <div>
                  <div className="spt-panel-label">Planta <span className="spt-panel-hint">(opcional)</span></div>
                  <input type="text" placeholder="P1" value={createOutletsForm.floor}
                    onChange={e => setCreateOutletsForm(f => ({ ...f, floor: e.target.value }))}
                    className="spt-field" style={{ width: 70 }} />
                </div>
                <button type="button" onClick={handleCreateOutlets} disabled={creatingOutlets}
                  className="spt-btn-success">
                  {creatingOutlets ? '⏳ Creando…' : `Crear ${selectedPorts.size} toma${selectedPorts.size !== 1 ? 's' : ''}`}
                </button>
                <button type="button" onClick={() => setShowCreateOutlets(false)}
                  className="spt-btn-secondary">
                  Cancelar
                </button>
              </div>
              <div className="spt-panel-hint" style={{ marginTop: 6 }}>
                Los códigos se generarán como: <strong>{createOutletsForm.code_prefix ? `${createOutletsForm.code_prefix}-` : ''}{ports.filter(p => selectedPorts.has(p.id)).slice(0,3).map(p => p.label || `P${p.port_number}`).join(', ')}{selectedPorts.size > 3 ? '…' : ''}</strong>
              </div>
              {createOutletsError && <div className="spt-panel-error">{createOutletsError}</div>}
            </div>
          )}
          {createOutletsOk && <div className="spt-panel-ok">{createOutletsOk}</div>}
        </div>
      )}

      {/* ── Switch Topology Canvas ── */}
      {isSwitch && showTopology && (
        <SwitchTopologyCanvas element={element} ports={ports} />
      )}

      {/* ── Port Card Grid ── */}
      <div className="spt-grid-section">
        <div className="spt-grid-header">
          <span>Mapa de puertos</span>
          <span className="spt-grid-hint">Haz clic en un puerto para configurarlo</span>
        </div>
        <div className="spt-grid">
          {portRows.map((row, ri) => (
            <div key={ri} className="spt-row">
              {row.map(port => (
                <button
                  key={port.id}
                  type="button"
                  className={`spt-port ${getPortColor(port)}${(bulkMode ? selectedPorts.has(port.id) : selectedPort?.id === port.id) ? ' spt-port--selected' : ''}${!isSwitch && port.wall_outlet ? ' spt-port--walled' : ''}`}
                  onClick={() => bulkMode ? togglePortInSelection(port) : handlePortClick(port)}
                  title={getPortTooltip(port)}
                >
                  {bulkMode && (
                    <span style={{ position: 'absolute', top: 1, left: 2, fontSize: 9, color: selectedPorts.has(port.id) ? '#7c3aed' : '#d1d5db' }}>
                      {selectedPorts.has(port.id) ? '☑' : '☐'}
                    </span>
                  )}
                  {!isSwitch && port.wall_outlet && (
                    <span className="spt-port-wall-dot" />
                  )}
                  {!isSwitch && getOriginBadge(port) && (() => {
                    const badge = getOriginBadge(port);
                    return (
                      <span style={{ position: 'absolute', top: 1, right: 2, fontSize: '0.48rem', lineHeight: 1, padding: '1px 3px', borderRadius: 999, background: badge.bg, color: badge.fg, fontWeight: 700 }}>
                        {badge.text}
                      </span>
                    );
                  })()}
                  <span className="spt-port-num">{port.port_number}</span>
                  {(() => { const c = getConnector(port); return <span className="spt-port-connector-badge" style={{ background: c.bg, color: c.fg }}>{c.abbr}</span>; })()}
                  {getPortLabel(port) && <span className="spt-port-dest">{getPortLabel(port)}</span>}
                  {isSwitch && vlanChip(port.vlan) && (
                    <span
                      className={`spt-port-vlan${vlanChip(port.vlan).trunk ? ' spt-port-vlan--trunk' : ''}`}
                      title={vlanTitle(port.vlan)}
                    >
                      {vlanChip(port.vlan).label}
                    </span>
                  )}
                  {isSwitch && port.speed && <span className="spt-port-speed">{port.speed}</span>}
                  {isSwitch && port.poe_enabled && (
                    <span
                      title="Puerto con PoE"
                      style={{ position: 'absolute', bottom: 1, right: 2, fontSize: '0.55rem', lineHeight: 1, color: '#f59e0b', fontWeight: 700 }}
                    >
                      ⚡
                    </span>
                  )}
                  {isSwitch && port.rstp_role && RSTP_ROLES[port.rstp_role] && (
                    <span style={{ position: 'absolute', top: 1, right: 2, fontSize: '0.5rem', fontWeight: 700, lineHeight: 1, padding: '0 2px', borderRadius: 3, background: RSTP_ROLES[port.rstp_role].bg, color: RSTP_ROLES[port.rstp_role].fg }}>
                      {RSTP_ROLES[port.rstp_role].abbr}
                    </span>
                  )}
                  {port.label && port.label !== `Port ${port.port_number}` && !(isSwitch && vlanChip(port.vlan)) && (
                    <span style={{ position: 'absolute', bottom: 1, left: 0, right: 0, fontSize: '0.55rem', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#7c3aed', lineHeight: 1 }}>
                      {port.label}
                    </span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ── Inline Port Editor ── */}
      {selectedPort && (
        <div className="spt-editor">
          <div className="spt-editor-header">
            <span className="spt-editor-num">Puerto <strong>#{selectedPort.port_number}</strong></span>
            {selectedPort.label && <span className="spt-editor-label">· {selectedPort.label}</span>}
            <span className={`spt-editor-badge ${hasConnection(selectedPort) || hasUplink(selectedPort) ? 'spt-badge--on' : 'spt-badge--off'}`}>
              {hasConnection(selectedPort) ? '● Conectado' : hasUplink(selectedPort) ? '● Uplink' : '○ Libre'}
            </span>
            <button type="button" className="spt-editor-close" onClick={() => setSelectedPort(null)} title="Cerrar editor">✕</button>
          </div>

          {/* Active connection banner */}
          {selectedPort.connection && (
            <div className="spt-conn-banner">
              <div className="spt-conn-path">
                <span className="spt-conn-arrow">⟶</span>
                <span>
                  <strong>{selectedPort.connection.other_end?.element_name || '—'}</strong>
                  {selectedPort.connection.other_end?.port_type !== 'wall_outlet' && selectedPort.connection.other_end?.port_number != null &&
                    <> · Puerto {selectedPort.connection.other_end.port_number}</>}
                  {selectedPort.connection.other_end?.port_type === 'wall_outlet' &&
                    <> · Toma {selectedPort.connection.other_end.label}</>}
                  {selectedPort.connection.cable_type && <span className="spt-conn-cable"> [{selectedPort.connection.cable_type}]</span>}
                  {selectedPort.connection.cable_color && <span className="spt-conn-color"> 🎨 {selectedPort.connection.cable_color}</span>}
                </span>
              </div>
              <button type="button" className="spt-disconnect-btn" onClick={handleDisconnect} disabled={saving}>✂️ Desconectar</button>
            </div>
          )}

          {/* Wall outlet cable (panel ports only) */}
          {!isSwitch && selectedPort.wall_outlet && (
            <div className="spt-wall-banner">
              <div className="spt-conn-path">
                <span className="spt-conn-arrow spt-wall-arrow">🏢</span>
                <span>
                  <strong>{selectedPort.wall_outlet.code}</strong>
                  {selectedPort.wall_outlet.name && <> · {selectedPort.wall_outlet.name}</>}
                  {selectedPort.wall_outlet.room && <> · {selectedPort.wall_outlet.room}</>}
                  {selectedPort.wall_outlet.floor && <span className="spt-conn-cable"> ({selectedPort.wall_outlet.floor})</span>}
                  {selectedPort.wall_outlet.location_name && <span className="spt-conn-color"> · {selectedPort.wall_outlet.location_name}</span>}
                </span>
              </div>
              <span className="spt-wall-hint">Cable de pared · gestionar en Canvas Cableado</span>
            </div>
          )}
          {!isSwitch && !selectedPort.wall_outlet && (
            <div className="spt-wall-empty">
              🏢 Sin toma de pared asignada · asignar desde Canvas Cableado
            </div>
          )}

          {!isSwitch && selectedPort.origin && (
            <div style={{ background: 'rgba(255,122,20,.12)', border: '1px solid rgba(249,115,22,.4)', borderRadius: 8, padding: '0.65rem 0.8rem', marginBottom: 10 }}>
              <div style={{ fontSize: '0.74rem', fontWeight: 700, color: '#9a3412', marginBottom: 6 }}>Ruta física del conector</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: '#7c2d12' }}>
                {getOriginPath(selectedPort)?.map((step, index, arr) => (
                  <React.Fragment key={`${step}-${index}`}>
                    <span style={{ background: '#ffedd5', border: '1px solid #fdba74', borderRadius: 999, padding: '2px 8px', fontWeight: 600 }}>{step}</span>
                    {index < arr.length - 1 && <span>→</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Keystone / slot status (panel only) */}
          {!isSwitch && (
            <div className="spt-field" style={{ marginBottom: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
                <input
                  type="checkbox"
                  checked={editForm.is_populated !== false}
                  onChange={e => setEditForm(f => ({ ...f, is_populated: e.target.checked, connection_type: e.target.checked ? (f.connection_type || 'rj45') : '' }))}
                  disabled={saving}
                />
                Keystone instalado
              </label>
              <p style={{ margin: '2px 0 0 24px', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                Desmarcar si el slot está físicamente vacío (sin conector montado)
              </p>
            </div>
          )}

          {!isSwitch && (
            <>
              <div className="spt-field">
                <label>Origen físico del puerto</label>
                <div className="spt-dest-toggle">
                  <button type="button" className={`spt-toggle-btn${editForm.origin_kind === 'wall_outlet' ? ' active' : ''}`} onClick={() => setOriginKind('wall_outlet')}>🏢 Pared</button>
                  <button type="button" className={`spt-toggle-btn${editForm.origin_kind === 'fiber_run' ? ' active' : ''}`} onClick={() => setOriginKind('fiber_run')}>🧵 Fibra</button>
                  <button type="button" className={`spt-toggle-btn${editForm.origin_kind === 'isp_circuit' ? ' active' : ''}`} onClick={() => setOriginKind('isp_circuit')}>🌐 WAN</button>
                  <button type="button" className={`spt-toggle-btn${editForm.origin_kind === 'isp_demarc' ? ' active' : ''}`} onClick={() => setOriginKind('isp_demarc')}>📍 Demarc</button>
                  <button type="button" className={`spt-toggle-btn${editForm.origin_kind === 'custom' ? ' active' : ''}`} onClick={() => setOriginKind('custom')}>✍ Otro</button>
                  <button type="button" className={`spt-toggle-btn${editForm.origin_kind === 'none' ? ' active' : ''}`} onClick={() => setOriginKind('none')}>⬜ Sin origen</button>
                </div>
                <div className="spt-hint">El origen describe qué llega por la parte trasera del panel. La conexión frontal se gestiona aparte.</div>
              </div>

              {editForm.origin_kind === 'wall_outlet' && (
                <div className="spt-field">
                  <label>Origen actual</label>
                  <div className="spt-hint">La toma de pared se deriva del cableado físico existente y se gestiona desde Canvas Cableado.</div>
                </div>
              )}

              {(editForm.origin_kind === 'fiber_run' || editForm.origin_kind === 'custom') && (
                <div className="spt-field">
                  <label>Etiqueta del origen</label>
                  <input className="spt-input" placeholder={editForm.origin_kind === 'fiber_run' ? 'Ej: Fibra troncal CPD-A' : 'Ej: Demarc auxiliar'} value={editForm.origin_label} onChange={e => setEditForm(f => ({ ...f, origin_label: e.target.value }))} />
                </div>
              )}

              {editForm.origin_kind === 'isp_circuit' && (
                <>
                  <div className="spt-field">
                    <label>Circuito WAN</label>
                    <select className="spt-select" value={editForm.origin_circuit_id} onChange={e => setEditForm(f => ({ ...f, origin_circuit_id: e.target.value, origin_demarc_id: '' }))}>
                      <option value="">— selecciona un circuito —</option>
                      {ispCircuits.map(circuit => <option key={circuit.id} value={circuit.id}>{circuit.label || circuit.circuit_id_provider || `Circuito ${circuit.id}`}</option>)}
                    </select>
                  </div>
                  <div className="spt-field">
                    <label>Alias visible</label>
                    <input className="spt-input" placeholder="Opcional" value={editForm.origin_label} onChange={e => setEditForm(f => ({ ...f, origin_label: e.target.value }))} />
                  </div>
                </>
              )}

              {editForm.origin_kind === 'isp_demarc' && (
                <>
                  <div className="spt-field">
                    <label>Punto demarc</label>
                    <select className="spt-select" value={editForm.origin_demarc_id} onChange={e => {
                      const demarcId = e.target.value;
                      const demarc = ispDemarcs.find(item => String(item.id) === demarcId);
                      setEditForm(f => ({
                        ...f,
                        origin_demarc_id: demarcId,
                        origin_circuit_id: demarc?.circuit_id ? String(demarc.circuit_id) : '',
                      }));
                    }}>
                      <option value="">— selecciona un demarc —</option>
                      {ispDemarcs.map(demarc => <option key={demarc.id} value={demarc.id}>{demarc.physical_id || demarc.label || demarc.rack_location || `Demarc ${demarc.id}`}</option>)}
                    </select>
                  </div>
                  <div className="spt-field">
                    <label>Alias visible</label>
                    <input className="spt-input" placeholder="Opcional" value={editForm.origin_label} onChange={e => setEditForm(f => ({ ...f, origin_label: e.target.value }))} />
                  </div>
                </>
              )}
            </>
          )}

          {/* Destination type */}
          <div className="spt-field">
            <label>Destino</label>
            <div className="spt-dest-toggle">
              <button type="button" className={`spt-toggle-btn${editForm.destination_type === 'wall'    ? ' active' : ''}`} onClick={() => setDestType('wall')}>🏢 Toma de pared</button>
              <button type="button" className={`spt-toggle-btn${editForm.destination_type === 'element' ? ' active' : ''}`} onClick={() => setDestType('element')}>🔗 Elemento</button>
              {!isSwitch && <button type="button" className={`spt-toggle-btn${editForm.destination_type === 'mirror' ? ' active' : ''}`} onClick={() => setDestType('mirror')}>🪞 Mirror</button>}
              {isSwitch && <button type="button" className={`spt-toggle-btn${editForm.destination_type === 'uplink' ? ' active' : ''}`} onClick={() => setDestType('uplink')}>⬆ Uplink</button>}
              <button type="button" className={`spt-toggle-btn${!editForm.destination_type ? ' active' : ''}`} onClick={() => setDestType('')}>⬜ Sin destino</button>
            </div>
          </div>

          {/* Wall outlet selector */}
          {editForm.destination_type === 'wall' && (
            <div className="spt-field">
              <label>Toma de pared</label>
              {loadingWallOutlets ? <span className="spt-hint">Cargando tomas…</span>
              : wallOutlets.length === 0 ? <p className="spt-hint">Sin tomas registradas.</p>
              : (
                <select className="spt-select" value={editForm.destination_outlet_id} onChange={e => setEditForm(f => ({ ...f, destination_outlet_id: e.target.value }))}>
                  <option value="">— selecciona una toma —</option>
                  {wallOutlets.map(o => {
                    const occupied = o.connection && o.connection.id !== selectedPort.connection?.id;
                    return <option key={o.id} value={o.id} disabled={occupied}>{o.code}{o.name ? ` · ${o.name}` : ''}{o.room ? ` (${o.room})` : ''}{occupied ? ' 🔴' : ''}</option>;
                  })}
                </select>
              )}
            </div>
          )}

          {/* Element + port selector */}
          {editForm.destination_type === 'element' && (
            <>
              <div className="spt-field">
                <label>Elemento destino</label>
                <label
                  className="spt-scope-toggle"
                  title="Por defecto se muestran los equipos de la misma raíz (sede/edificio). Marca para ver también otras ubicaciones."
                >
                  <input
                    type="checkbox"
                    checked={showAllLocations}
                    onChange={e => setShowAllLocations(e.target.checked)}
                  />
                  <span>Mostrar elementos de todas las ubicaciones</span>
                  <em>{destinationElements.length + destinationAps.length} destinos</em>
                </label>
                <select className="spt-select" value={editForm.destination_element_id} onChange={e => setEditForm(f => ({ ...f, destination_element_id: e.target.value, destination_port_id: '' }))}>
                  <option value="">— selecciona un elemento —</option>
                  {destinationGroups.map(grupo => (
                    <React.Fragment key={grupo.key}>
                      {/* Título de grupo como option disabled: <optgroup> se
                          pintaba con fondo blanco en Windows y rompía el tema. */}
                      <option value={`__grupo-${grupo.key}`} disabled className="spt-select-group">
                        ── {grupo.label} ──
                      </option>
                      {grupo.elements.map(el => (
                        <option key={`el-${el.id}`} value={`el:${el.id}`}>[{el.element_type}] {el.name || el.code}</option>
                      ))}
                    </React.Fragment>
                  ))}
                  {destinationAps.length > 0 && (
                    <>
                      <option value="__grupo-ap" disabled className="spt-select-group">
                        ── Puntos de acceso WiFi ──
                      </option>
                      {destinationAps.map(a => (
                        <option key={`ap-${a.id}`} value={`ap:${a.id}`}>
                          [AP] {a.name}{a.location_id ? ` · ${locationPath(locationMap, a.location_id)}` : ''}
                        </option>
                      ))}
                    </>
                  )}
                  {!destinationGroups.length && !destinationAps.length && (
                    <option value="" disabled>
                      No hay elementos en esta ubicación; marca «Mostrar todas las ubicaciones»
                    </option>
                  )}
                </select>
              </div>
              {editForm.destination_element_id && (
                <div className="spt-field">
                  <label>Puerto de destino</label>
                  {loadingDestPorts ? <span className="spt-hint">Cargando puertos…</span>
                  : destPorts.length === 0 ? <p className="spt-hint">Sin puertos.</p>
                  : (
                    <select className="spt-select" value={editForm.destination_port_id} onChange={e => setEditForm(f => ({ ...f, destination_port_id: e.target.value }))}>
                      <option value="">— selecciona un puerto —</option>
                      {destPorts.map(p => {
                        const occupied = p.connection && p.id !== parseInt(editForm.destination_port_id);
                        const label = p.component_name ? `${p.component_name} · P${p.port_number}${p.label ? ` (${p.label})` : ''}` : `P${p.port_number}${p.label ? ` · ${p.label}` : ''}`;
                        return <option key={p.id} value={p.id} disabled={occupied}>{label} {occupied ? '🔴 ocupado' : '🟢'}</option>;
                      })}
                    </select>
                  )}
                </div>
              )}
            </>
          )}

          {/* Mirror panel selector (panels from all racks) */}
          {editForm.destination_type === 'mirror' && (
            <>
              <div className="spt-field">
                <label>Panel destino (otro rack)</label>
                {loadingMirrorPanels ? <span className="spt-hint">Cargando paneles…</span>
                : mirrorPanels.length === 0 ? <p className="spt-hint">No hay otros patch panels registrados.</p>
                : (
                  <select className="spt-select" value={editForm.destination_element_id} onChange={e => setEditForm(f => ({ ...f, destination_element_id: e.target.value, destination_port_id: '' }))}>
                    <option value="">— selecciona un panel —</option>
                    {mirrorPanels.map(el => <option key={el.id} value={el.id}>{el.rack_name ? `[${el.rack_name}] ` : ''}{el.name || el.code}</option>)}
                  </select>
                )}
              </div>
              {editForm.destination_element_id && (
                <div className="spt-field">
                  <label>Puerto espejo</label>
                  {loadingDestPorts ? <span className="spt-hint">Cargando puertos…</span>
                  : destPorts.length === 0 ? <p className="spt-hint">Sin puertos.</p>
                  : (
                    <select className="spt-select" value={editForm.destination_port_id} onChange={e => setEditForm(f => ({ ...f, destination_port_id: e.target.value }))}>
                      <option value="">— selecciona un puerto —</option>
                      {destPorts.map(p => {
                        const occupied = p.connection && p.id !== parseInt(editForm.destination_port_id);
                        const label = `P${p.port_number}${p.label ? ` · ${p.label}` : ''}`;
                        return <option key={p.id} value={p.id} disabled={occupied}>{label} {occupied ? '🔴 ocupado' : '🟢'}</option>;
                      })}
                    </select>
                  )}
                </div>
              )}
            </>
          )}

          {/* Uplink free text */}
          {editForm.destination_type === 'uplink' && (
            <div className="spt-field">
              <label>Descripción uplink</label>
              <input className="spt-input" placeholder="Ej: Core SW-01 port 48, ISP router…" value={editForm.uplink_text} onChange={e => setEditForm(f => ({ ...f, uplink_text: e.target.value }))} />
            </div>
          )}

          {/* Cable + network details */}
          <div className="spt-fields-grid">
            <div className="spt-field">
              <label>Conector</label>
              <select className="spt-select" value={editForm.connection_type} onChange={e => setEditForm(f => ({ ...f, connection_type: e.target.value }))}
                disabled={!isSwitch && editForm.is_populated === false}>
                {!isSwitch && <option value="">— sin conector —</option>}
                {CONNECTOR_TYPES.map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
              </select>
              {!isSwitch && editForm.is_populated === false && <div className="spt-hint">Instala un keystone para poder definir el conector.</div>}
            </div>
            <div className="spt-field">
              <label>Tipo de cable</label>
              <select className="spt-select" value={editForm.cable_type} onChange={e => setEditForm(f => ({ ...f, cable_type: e.target.value }))}>
                {CABLE_TYPES.map(t => <option key={t} value={t}>{t || '— sin especificar —'}</option>)}
              </select>
            </div>
            <div className="spt-field">
              <label>Color cable</label>
              <select className="spt-select" value={editForm.cable_color} onChange={e => setEditForm(f => ({ ...f, cable_color: e.target.value }))}>
                {CABLE_COLORS.map(c => <option key={c} value={c}>{c || '— sin color —'}</option>)}
              </select>
            </div>
            <div className="spt-field">
              <label>Longitud (m)</label>
              <input className="spt-input" type="number" min="0" step="0.5" placeholder="0.5" value={editForm.cable_length_m} onChange={e => setEditForm(f => ({ ...f, cable_length_m: e.target.value }))} />
            </div>
            {isSwitch && (<>
              <div className="spt-field">
                <label>VLAN</label>
                <input list="port-vlan-list" className="spt-input" placeholder="100, trunk…" value={editForm.vlan} onChange={e => setEditForm(f => ({ ...f, vlan: e.target.value }))} />
                <datalist id="port-vlan-list">
                  {ipamVlans.map(v => <option key={v.id} value={String(v.vlan_id)}>{v.vlan_id} — {v.name}</option>)}
                </datalist>
              </div>
              <div className="spt-field">
                <label>Velocidad</label>
                <select className="spt-select" value={editForm.speed} onChange={e => setEditForm(f => ({ ...f, speed: e.target.value }))}>
                  {SWITCH_SPEEDS.map(s => <option key={s} value={s}>{s || '— auto —'}</option>)}
                </select>
              </div>
              <div className="spt-field">
                <label>Rol RSTP</label>
                <select className="spt-select" value={editForm.rstp_role} onChange={e => setEditForm(f => ({ ...f, rstp_role: e.target.value }))}>
                  <option value="">— sin rol —</option>
                  {Object.entries(RSTP_ROLES).map(([key, r]) => (
                    <option key={key} value={key}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div className="spt-field">
                <label>PoE</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', paddingTop: 6 }}>
                  <input
                    type="checkbox"
                    checked={!!editForm.poe_enabled}
                    onChange={e => setEditForm(f => ({ ...f, poe_enabled: e.target.checked }))}
                  />
                  ⚡ Entrega PoE
                </label>
                {!(poeCapable ?? element?.poe_capable) && (
                  <div style={{ fontSize: '0.7rem', color: '#b45309', marginTop: 2 }}>
                    Este switch no está marcado como PoE (pestaña ⚡Alimentación).
                  </div>
                )}
              </div>
            </>)}
          </div>

          <div className="spt-field">
            <label>Notas</label>
            <input className="spt-input" placeholder="Notas opcionales…" value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} />
          </div>

          {saveError && <div className="spt-error">{saveError}</div>}

          <div className="spt-editor-actions">
            <button type="button" className="spt-btn-cancel" onClick={() => setSelectedPort(null)}>Cancelar</button>
            <button type="button" className="spt-btn-save" onClick={handleSave} disabled={saving}>
              {saving ? '⏳ Guardando…' : '💾 Guardar puerto'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SwitchPortsTab;
